import {
  createStoreSlice as createSlice,
  type PayloadAction,
} from '@/store/core';
import { RootState } from '@/store';
import { LoginFormField } from '@/forms/typings';
import { AuthKey } from '@/auth';
import { StoreError } from '@/types/storeError.interface';
import {
  AuthVariables,
  IndexedAuthVariables,
  PotentialTerminationReason,
} from '../typings';

interface AuthState {
  token: string | null;
  loading: boolean;
  error: StoreError | null;
  willLogout: boolean;

  currentKey: AuthKey | null;
  authVariables: IndexedAuthVariables;
  potentialTerminationReasons: PotentialTerminationReason[] | null;
}

const initialState: AuthState = {
  token: null,
  loading: false,
  error: null,
  willLogout: false,

  currentKey: null,
  authVariables: {},
  potentialTerminationReasons: null,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginLoading: state => {
      state.loading = true;
    },
    loginSuccess: (
      state,
      action: PayloadAction<{ key: AuthKey; token: string }>,
    ) => {
      state.loading = false;
      state.error = null;
      state.token = action.payload.token;
    },
    loginFailure: (state, action: PayloadAction<StoreError>) => {
      state.loading = false;
      state.error = action.payload;
    },
    logout: state => {
      const newAuthVariables = { ...state.authVariables };
      delete newAuthVariables[state.currentKey as AuthKey];
      state.authVariables = newAuthVariables;
      state.token = null;
      state.currentKey = null;
      state.willLogout = false;
    },
    setWillLogout: state => {
      state.willLogout = true;
    },
    clearWillLogout: state => {
      state.willLogout = false;
    },
    setIdentity: (
      state,
      action: PayloadAction<{
        key: AuthKey;
        identity: AuthVariables['identity'];
      }>,
    ) => {
      state.loading = false;
      state.authVariables[action.payload.key] = {
        ...state.authVariables[action.payload.key],
        identity: action.payload.identity,
      };
      state.currentKey = action.payload.key;
    },
    setPotentialTerminationDetail: (
      state,
      action: PayloadAction<{
        key: AuthKey;
        terminationDetail?: AuthVariables['terminationDetail'];
      }>,
    ) => {
      state.loading = false;
      state.authVariables[action.payload.key] = {
        ...state.authVariables[action.payload.key],
        terminationDetail: action.payload.terminationDetail,
      };
      state.currentKey = action.payload.key;
    },
    setTerminationSkipped: (
      state,
      action: PayloadAction<{
        key: AuthKey;
        terminationSkipped: AuthVariables['terminationSkipped'];
      }>,
    ) => {
      state.loading = false;
      state.authVariables[action.payload.key] = {
        ...state.authVariables[action.payload.key],
        terminationSkipped: action.payload.terminationSkipped,
      };
      state.currentKey = action.payload.key;
    },
    setTerminationUserData: (
      state,
      action: PayloadAction<{
        key: AuthKey;
        terminationUserData: AuthVariables['terminationUserData'];
      }>,
    ) => {
      state.loading = false;
      state.authVariables[action.payload.key] = {
        ...state.authVariables[action.payload.key],
        terminationUserData: action.payload.terminationUserData,
      };
      state.currentKey = action.payload.key;
    },
    fetchPotentialTerminationReasonsSuccess: (
      state,
      action: PayloadAction<PotentialTerminationReason[]>,
    ) => {
      state.loading = false;
      state.potentialTerminationReasons = action.payload;
    },
    fetchQuestionsSuccess: (
      state,
      action: PayloadAction<{
        key: AuthKey;
        questions: AuthVariables['questions'];
        providerFullName: AuthVariables['providerFullName'];
        providerAddress: AuthVariables['providerAddress'];
        taxonomyName: AuthVariables['taxonomyName'];
        isInstitution: AuthVariables['isInstitution'];
      }>,
    ) => {
      state.loading = false;
      state.error = null;
      state.authVariables[action.payload.key] = {
        ...state.authVariables[action.payload.key],
        questions: action.payload.questions,
        providerFullName: action.payload.providerFullName,
        providerAddress: action.payload.providerAddress,
        taxonomyName: action.payload.taxonomyName,
        isInstitution: action.payload.isInstitution,
      };
    },
    fetchCaqhInfoSuccess: (
      state,
      action: PayloadAction<{
        key: AuthKey;
        caqhInfo: AuthVariables['caqhInfo'];
      }>,
    ) => {
      state.loading = false;
      state.error = null;
      state.authVariables[action.payload.key] = {
        ...state.authVariables[action.payload.key],
        caqhInfo: action.payload.caqhInfo,
      };
    },
    fetchAuthFailure: (
      state,
      action: PayloadAction<{
        key: AuthKey;
        error: StoreError;
      }>,
    ) => {
      state.loading = false;
      state.currentKey = action.payload.key;
      state.authVariables[action.payload.key] = {
        ...state.authVariables[action.payload.key],
        error: action.payload.error,
      };
    },
    setQuestionValue: (
      state,
      action: PayloadAction<{
        key: AuthKey;
        questionId: string;
        value: string;
      }>,
    ) => {
      const newQuestions = state.authVariables[
        action.payload.key
      ]?.questions?.map(q => {
        if (q.fieldId === action.payload.questionId) {
          return {
            ...q,
            field: {
              ...q.field,
              value: action.payload.value,
            },
          } as LoginFormField;
        }
        return q;
      });

      state.authVariables[action.payload.key] = {
        ...state.authVariables[action.payload.key],
        questions: newQuestions,
      };
    },
    fetchPlanListSuccess: (
      state,
      action: PayloadAction<{
        key: AuthKey;
        planList: AuthVariables['planList'];
        providerFullName: AuthVariables['providerFullName'];
        providerAddress: AuthVariables['providerAddress'];
        taxonomyName: AuthVariables['taxonomyName'];
        isInstitution: AuthVariables['isInstitution'];
      }>,
    ) => {
      state.loading = false;
      state.error = null;
      state.authVariables[action.payload.key] = {
        ...state.authVariables[action.payload.key],
        planList: action.payload.planList,
        providerFullName: action.payload.providerFullName,
        providerAddress: action.payload.providerAddress,
        taxonomyName: action.payload.taxonomyName,
        isInstitution: action.payload.isInstitution,
      };
    },
  },
});

export const {
  fetchQuestionsSuccess,
  fetchAuthFailure,
  loginLoading,
  loginSuccess,
  loginFailure,
  logout,
  setWillLogout,
  clearWillLogout,
  setIdentity,
  setPotentialTerminationDetail,
  setTerminationSkipped,
  setTerminationUserData,
  setQuestionValue,
  fetchPotentialTerminationReasonsSuccess,
  fetchCaqhInfoSuccess,
  fetchPlanListSuccess,
} = authSlice.actions;

export const selectAuth = (state: RootState) =>
  state[authSlice.name] as typeof initialState;
